import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Mail, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BellIcon, SendIcon, XIcon } from "../../components/icons";
import { pageRootTestIds } from "../../testing/pageRootTestIds";

const ConfirmSendModal = ({
  isOpen,
  onClose,
  onConfirm,
  recipientCount,
  totalTo,
  totalCc,
  isSending,
  isNotifyAll = false,
}) => {
  const { t } = useTranslation();

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        {/* Modal panel */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-90 translate-y-4"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-90 translate-y-4"
            >
              <Dialog.Panel
                className="w-full max-w-lg overflow-hidden rounded-2xl bg-cfSecondary shadow-2xl ring-1 ring-black/5"
                id={pageRootTestIds.notifyConfirmSendModal}
              >
                <div className="relative">
                  <button
                    onClick={onClose}
                    id="notify-confirm-send-modal-close"
                    aria-label={t("workspace:close")}
                    className="absolute top-4 right-4 p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200 focus:outline-none"
                  >
                    <XIcon size={16} className="inline-flex" aria-hidden />
                  </button>
                </div>

                <div className="px-8 pt-7 pb-8">
                  <div className="flex flex-col items-center text-center mb-7">
                    <div className="relative mb-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cfPrimary/10 to-cfPrimaryDark/10 flex items-center justify-center ring-4 ring-cfPrimary/5">
                        {isNotifyAll ? (
                          <BellIcon
                            size={28}
                            className="inline-flex text-cfPrimary"
                            aria-hidden
                          />
                        ) : (
                          <SendIcon
                            size={28}
                            className="inline-flex text-cfPrimary"
                            aria-hidden
                          />
                        )}
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-cfPrimary flex items-center justify-center shadow-lg">
                        <Mail className="h-3 w-3 text-cfSecondary" />
                      </div>
                    </div>
                    <Dialog.Title className="text-xl font-bold text-gray-900">
                      {t("workspace:confirm_send")}
                    </Dialog.Title>
                    <p className="text-sm text-gray-500 mt-1.5 max-w-xs">
                      {isNotifyAll ? (
                        <>
                          {t("workspace:notify_all_confirmation")}{" "}
                          <span className="font-semibold text-gray-700">
                            {recipientCount}
                          </span>{" "}
                          {recipientCount > 1
                            ? t("workspace:recipients")
                            : t("workspace:recipient")}
                        </>
                      ) : (
                        <>
                          {t("workspace:about_to_send_email_notifications")}{" "}
                          <span className="font-semibold text-gray-700">
                            {recipientCount}
                          </span>{" "}
                          {recipientCount > 1
                            ? t("workspace:recipients")
                            : t("workspace:recipient")}
                        </>
                      )}
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-7">
                    <div className="flex flex-col items-center p-4 rounded-xl bg-gradient-to-b from-blue-50 to-blue-50/30 border border-blue-100">
                      <div className="w-9 h-9 rounded-lg bg-cfPrimary/10 flex items-center justify-center mb-2">
                        <Users className="h-4 w-4 text-cfPrimary" />
                      </div>
                      <span className="text-2xl font-bold text-gray-900">
                        {recipientCount}
                      </span>
                      <span className="text-xs text-gray-800 font-medium mt-0.5">
                        {recipientCount > 1
                          ? t("workspace:recipients")
                          : t("workspace:caps_recipient")}
                      </span>
                    </div>

                    <div className="flex flex-col items-center p-4 rounded-xl bg-gradient-to-b from-emerald-50 to-emerald-50/30 border border-emerald-100">
                      <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center mb-2">
                        <Mail className="h-4 w-4 text-emerald-600" />
                      </div>
                      <span className="text-2xl font-bold text-gray-900">
                        {totalTo}
                      </span>
                      <span className="text-xs text-gray-800 font-medium mt-0.5">
                        {t("workspace:to")}
                      </span>
                    </div>

                    <div className="flex flex-col items-center p-4 rounded-xl bg-gradient-to-b from-amber-50 to-amber-50/30 border border-amber-100">
                      <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center mb-2">
                        <Mail className="h-4 w-4 text-amber-600" />
                      </div>
                      <span className="text-2xl font-bold text-gray-900">
                        {totalCc}
                      </span>
                      <span className="text-xs text-gray-800 font-medium mt-0.5">
                        CC
                      </span>
                    </div>
                  </div>
                  <div className="border-t border-gray-100 pt-3 mb-6">
                    <p className="text-sm text-gray-600 text-center">
                      {t("workspace:would_you_like_to_continue")}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={onClose}
                      id="notify-confirm-send-modal-cancel"
                      disabled={isSending}
                      className="flex-1 px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-200"
                    >
                      {t("workspace:cancel")}
                    </button>
                    <button
                      onClick={onConfirm}
                      id="notify-confirm-send-modal-confirm"
                      disabled={isSending}
                      className={`flex-1 px-5 py-2.5 rounded-xl bg-cfPrimary text-cfSecondary font-medium hover:bg-cfPrimaryDark active:bg-cfPrimaryDarkBlue transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cfPrimary/50 flex items-center justify-center gap-2 ${
                        isSending ? "opacity-75 cursor-not-allowed" : ""
                      }`}
                    >
                      <SendIcon size={16} className="inline-flex" aria-hidden />
                      {isSending
                        ? t("workspace:sending")
                        : t("workspace:yes_send")}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ConfirmSendModal;
